import React, { useEffect, useState } from "react";
import { db } from "../utils/firebase";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { Box, Typography, Avatar, Button, Paper, Stack, CircularProgress, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

export default function AdminProfilePictureApprovals() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState({});
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      setError("");
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const usersArr = usersSnap.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
        setUsers(usersArr.filter(u => u.pendingProfilePicture && u.profilePictureApproved === false));
      } catch (err) {
        setError(err?.message || "Unable to load pending profile pictures.");
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const handleApprove = async (user) => {
    setUpdating(prev => ({ ...prev, [user.uid]: true }));
    await updateDoc(doc(db, "users", user.uid), {
      profilePicture: user.pendingProfilePicture,
      profilePictureApproved: true,
      pendingProfilePicture: null
    });
    setUsers(users => users.filter(u => u.uid !== user.uid));
    setUpdating(prev => ({ ...prev, [user.uid]: false }));
  };

  const handleReject = async (user) => {
    setUpdating(prev => ({ ...prev, [user.uid]: true }));
    await updateDoc(doc(db, "users", user.uid), {
      pendingProfilePicture: null,
      profilePictureApproved: false
    });
    setUsers(users => users.filter(u => u.uid !== user.uid));
    setUpdating(prev => ({ ...prev, [user.uid]: false }));
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Profile Picture Approvals</Typography>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {users.length === 0 ? (
        <Typography>No pending profile pictures.</Typography>
      ) : (
        <Stack spacing={2}>
          {users.map(user => (
            <Paper
              key={user.uid}
              sx={{
                p: 2,
                display: 'flex',
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={user.pendingProfilePicture}
                  alt={(user.name || user.email || 'User') + ' – pending'}
                  sx={{ width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 }, cursor: 'pointer' }}
                  onClick={() => setPreviewSrc(user.pendingProfilePicture)}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                    {user.name || user.email || user.uid}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                  disabled={updating[user.uid]}
                  onClick={() => handleApprove(user)}
                >
                  {updating[user.uid] ? 'Approving…' : 'Approve'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                  disabled={updating[user.uid]}
                  onClick={() => handleReject(user)}
                >
                  {updating[user.uid] ? 'Rejecting…' : 'Reject'}
                </Button>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!previewSrc} onClose={() => setPreviewSrc(null)} maxWidth="sm" fullWidth>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setPreviewSrc(null)} aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 1, sm: 2 } }}>
          {previewSrc && (
            <Box component="img"
              src={previewSrc}
              alt="Profile Picture Preview"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: { xs: '70vh', sm: '80vh' },
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: 1
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
